import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { cn, useTranslation } from "@/packages/utils";
import { Button } from "@/components/ui/primitives";
import { springs } from "@/components/ui/motion";
import type { PiiRole } from "./types";

// ─── Props ──────────────────────────────────────────────────────────────────

interface StepPiiRoleProps {
  selectedRole: PiiRole | null;
  onSelect: (role: PiiRole) => void;
  onNext: () => void;
  onBack: () => void;
}

// ─── Role Options ────────────────────────────────────────────────────────────

const ROLE_OPTIONS: PiiRole[] = ["controller", "processor", "both"];

// ─── Component ──────────────────────────────────────────────────────────────

export function StepPiiRole({
  selectedRole,
  onSelect,
  onNext,
  onBack,
}: StepPiiRoleProps) {
  const { t } = useTranslation(["audits", "common"]);

  return (
    <div className="flex flex-col gap-6 py-4">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-sm font-medium text-primary mb-1">
          {t("audits:wizard.piiRole.title")}
        </h3>
        <p className="text-[12px] text-muted">
          {t("audits:wizard.piiRole.description")}
        </p>
      </div>

      {/* Role Cards */}
      <div className="grid gap-2">
        {ROLE_OPTIONS.map((role) => (
          <RoleCard
            key={role}
            role={role}
            isSelected={selectedRole === role}
            onSelect={() => onSelect(role)}
          />
        ))}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2">
        <Button variant="secondary" onClick={onBack}>
          {t("audits:wizard.actions.backToDetails")}
        </Button>
        <Button
          variant="primary"
          onClick={onNext}
          disabled={selectedRole === null}
        >
          {t("audits:wizard.actions.nextProfiling")}
        </Button>
      </div>
    </div>
  );
}

// ─── RoleCard ────────────────────────────────────────────────────────────────

interface RoleCardProps {
  role: PiiRole;
  isSelected: boolean;
  onSelect: () => void;
}

function RoleCard({ role, isSelected, onSelect }: RoleCardProps) {
  const { t } = useTranslation("audits");

  const labelKey =
    role === "both"
      ? "wizard.piiRole.both"
      : role === "controller"
        ? "wizard.piiRole.controller"
        : "wizard.piiRole.processor";

  const descKey =
    role === "both"
      ? "wizard.piiRole.bothDescription"
      : role === "controller"
        ? "wizard.piiRole.controllerDescription"
        : "wizard.piiRole.processorDescription";

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "relative flex items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-all duration-150",
        isSelected
          ? "border-accent bg-accent/5 ring-1 ring-accent/30"
          : "border-border hover:border-accent/40",
      )}
    >
      {/* Radio indicator */}
      <div
        className={cn(
          "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
          isSelected ? "border-accent bg-accent" : "border-border",
        )}
      >
        {isSelected && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={springs.bouncy}
          >
            <Check className="h-2.5 w-2.5 text-white" />
          </motion.div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <span
          className={cn(
            "text-[12px] font-semibold",
            isSelected ? "text-accent" : "text-primary",
          )}
        >
          {t(labelKey)}
        </span>
        <span className="text-[11px] text-muted">{t(descKey)}</span>
      </div>
    </button>
  );
}
